<template>
    <div class="redesign-export-data">
        <VcModal
            :visible="isOpen"
            :header="t('general.exportData')"
            width="50%"
            :dismissable-mask="false"
            close-on-escape
            @close="closeModal"
        >
            <template #default>
                <div>
                    <div class="mb-4">
                        {{ t('widget.export.prepareExport') + ' ' + widget.Title }}
                    </div>
                    <div class="mb-3">
                        {{ t('widget.export.fileName') }}
                    </div>
                    <div class="mb-4">
                        <VcInput v-model="widgetTitle" />
                    </div>
                    <div class="mb-3">
                        {{ t('report.format') }}
                    </div>
                    <div>
                        <VcRadioButtonsGroup
                            v-model="selectedExportOption"
                            :radios="exportRadioOptions"
                            :config="{ valueKey: 'value', labelKey: 'label' }"
                            class="mb-4"
                        />
                    </div>
                    <RedesignExportDataManage
                        ref="exportDataManageRef"
                        v-model="columnsToExport"
                        :visible-columns="localVisibleColumnsComputed"
                        :hidden-columns="localHiddenColumnsComputed"
                    />
                </div>
            </template>
            <template #footer>
                <div class="flex gap-2">
                    <VcButton
                        type="outline"
                        color="secondary"
                        @click="closeModal"
                    >
                        {{ t('common.cancel') }}
                    </VcButton>
                    <VcButton @click="exportData">
                        {{ t('widget.export.export') }}
                    </VcButton>
                </div>
            </template>
        </VcModal>
    </div>
</template>

<script lang="ts" setup>
import { getWidgetDateInterval } from '@/helpers/widgetUtils.js'
import { timeFormatter } from '@/helpers/timeFormatter.js'
import { getInitialTime } from '@/util/timeUtils.js'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcInput, VcButton, VcRadioButtonsGroup, VcModal } from '@voicenter-team/voicenter-ui-plus'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignExportDataManage
    from '@/components/RedesignComponents/RedesignTableComponents/RedesignExportDataManage.vue'
import * as XLSX from 'xlsx'
import { Call } from '@/types/extension'
import { PropNames } from '@/enum/realtimeTableConfig'
import { ColumnsToExport, WidgetExtended, WidgetTemplateColumnWithConfig } from '@/types/dashboard'

type Headers = Record<string, string>;

const EXPORT_OPTIONS = {
    xlsx: {
        label: '.xlsx',
        visibleLabel: 'Exel'
    },
    csv: {
        label: '.csv',
        visibleLabel: 'CSV'
    }
}

const props = withDefaults(
    defineProps<{
        widget: WidgetExtended
        tableData: Record<string, string | number>[]
    }>(),
    {
        tableData: () => []
    }
)
const { t } = useI18n()

//Emits
const emit = defineEmits<{
    (e: 'on-close-export-modal'): void
}>()

//State
const { closeWithConfirm } = useConfirmBeforeClose()
const isOpen = ref(false)
const columnsToExport = ref<ColumnsToExport>({})
const localAllColumns = ref<WidgetTemplateColumnWithConfig[]>([])
const selectedExportOption = ref(EXPORT_OPTIONS.xlsx.label)
const widgetTitle = ref(props.widget.Title)

const exportRadioOptions = [
    {
        value: EXPORT_OPTIONS.xlsx.label,
        label: EXPORT_OPTIONS.xlsx.visibleLabel
    },
    {
        value: EXPORT_OPTIONS.csv.label,
        label: EXPORT_OPTIONS.csv.visibleLabel
    }
]
const exportDataManageRef = ref<typeof RedesignExportDataManage>()

//Computed
const localVisibleColumnsComputed = computed<WidgetTemplateColumnWithConfig[]>(() => {
    if (!localAllColumns.value.length) {
        return []
    }
    return localAllColumns.value
        .filter(column => columnsToExport.value[column.prop]?.addToExport)
        .sort((a, b) => {
            const orderA = columnsToExport.value[a.prop].sortOrder
            const orderB = columnsToExport.value[b.prop].sortOrder
            return orderA - orderB
        })
})

const localHiddenColumnsComputed = computed(() => {
    if (!localAllColumns.value.length) {
        return []
    }
    return localAllColumns.value.filter(column => !columnsToExport.value[column.prop]?.addToExport)
})

const getFileName = () => {
    const fileName = widgetTitle.value || t('widget.title')
    const timeRange = getWidgetDateInterval(props.widget, 'DD-MM-YYYY')

    return fileName + ' ' + timeRange + selectedExportOption.value
}

const exportData = () => {
    const fileName = getFileName()
    const headers = localVisibleColumnsComputed.value.reduce((acc: Headers, column: WidgetTemplateColumnWithConfig) => {
        acc[column.prop] = column.label.fullTextLabel
        return acc
    }, {})

    const mappedData = props.tableData.map(row => {

        const formattedRow: Record<string, string | number> = {}

        Object.keys(headers).forEach(key => {
            formattedRow[key] = row[key]
        })

        if (formattedRow[PropNames.STATUS_DURATION]) {
            formattedRow[PropNames.STATUS_DURATION] = timeFormatter(+formattedRow[PropNames.STATUS_DURATION])
        }
        if (formattedRow[PropNames.CALL_INFO]) {
            formattedRow[PropNames.CALL_INFO] = t('widget.table.noCallers')
        }
        if (formattedRow[PropNames.CALL_INFO] && row.extension.calls.length) {
            formattedRow[PropNames.CALL_INFO] = row.extension.calls.reduce((acc: string, call: Call) => {
                const initialTime = getInitialTime(call.callStarted_UTC_CLIENT)
                acc += timeFormatter(initialTime, false) + ' ' + call.callType
                return acc
            }, '')
        }

        return formattedRow
    })

    const dataWithHeaders = [ headers, ...mappedData ]
    const worksheet = XLSX.utils.json_to_sheet(dataWithHeaders, { skipHeader: true })
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet)
    XLSX.writeFile(workbook, fileName)
    forceCloseModal()
}

function openModal (visibleColumns: Array<WidgetTemplateColumnWithConfig>, hiddenColumns: Array<WidgetTemplateColumnWithConfig>) {
    localAllColumns.value = [ ...visibleColumns, ...hiddenColumns ]
    visibleColumns.forEach((column, i) => {
        columnsToExport.value[column.prop] = {
            prop: column.prop,
            addToExport: true,
            sortOrder: i
        }
    })
    hiddenColumns.forEach((column) => {
        columnsToExport.value[column.prop] = {
            prop: column.prop,
            addToExport: false,
            sortOrder: 0
        }
    })
    isOpen.value = true
}

function forceCloseModal () {
    isOpen.value = false
    localAllColumns.value = []
    columnsToExport.value = {}
    exportDataManageRef.value?.clearData()
    emit('on-close-export-modal')
}

function closeModal () {
    closeWithConfirm(forceCloseModal)
}

defineExpose({
    open: openModal,
    close: forceCloseModal
})
</script>
